import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  listInquiries,
  updateInquiryStatus,
  deleteInquiry,
} from "@/lib/admin.functions";
import { toast } from "sonner";
import {
  Loader2,
  Trash2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Users as UsersIcon,
} from "lucide-react";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/inquiries/$id")({
  component: InquiryDetailPage,
});

type Inquiry = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  event_date: string | null;
  location: string | null;
  guests: number | null;
  message: string | null;
  products: { slug: string; name: string; image: string; quantity?: number }[];
  status: string;
  created_at: string;
};

const STATUS_LABELS: Record<string, string> = {
  new: "Ny",
  in_progress: "Pågående",
  done: "Klar",
  archived: "Arkiverad",
};
const STATUS_STYLES: Record<string, string> = {
  new: "bg-accent text-accent-foreground",
  in_progress: "bg-primary text-primary-foreground",
  done: "bg-secondary text-foreground",
  archived: "bg-muted text-muted-foreground",
};

function InquiryDetailPage() {
  const { id } = Route.useParams();
  const list = useServerFn(listInquiries);
  const upd = useServerFn(updateInquiryStatus);
  const del = useServerFn(deleteInquiry);
  const navigate = useNavigate();
  const [i, setI] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    list({})
      .then((items) => {
        const f = (items as unknown as Inquiry[]).find((x) => x.id === id);
        if (!f) throw new Error("Förfrågan hittades inte");
        setI(f);
      })
      .catch((e: any) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const setStatus = async (status: string) => {
    if (!i) return;
    try {
      await upd({ data: { id: i.id, status: status as any } });
      setI({ ...i, status });
      toast.success("Status uppdaterad");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const remove = async () => {
    if (!i) return;
    if (!confirm("Ta bort förfrågan?")) return;
    try {
      await del({ data: { id: i.id } });
      toast.success("Borttagen");
      navigate({ to: "/admin/inquiries" });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!i) return <p className="text-sm text-muted-foreground">Hittades inte.</p>;

  return (
    <>
      <PageHeader title={i.name} backTo="/admin/inquiries">
        <a
          href={`mailto:${i.email}?subject=Re: Din förfrågan`}
          className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-bold"
        >
          <Mail className="h-4 w-4" /> Svara
        </a>
        <button
          onClick={remove}
          className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-bold"
        >
          <Trash2 className="h-4 w-4" /> Ta bort
        </button>
      </PageHeader>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
              STATUS_STYLES[i.status] ?? "bg-secondary"
            }`}
          >
            {STATUS_LABELS[i.status] ?? i.status}
          </span>
          <span className="text-xs text-muted-foreground">
            Skapad {new Date(i.created_at).toLocaleString("sv-SE")}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <a href={`mailto:${i.email}`} className="hover:underline">
              {i.email}
            </a>
          </div>
          {i.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a href={`tel:${i.phone}`} className="hover:underline">
                {i.phone}
              </a>
            </div>
          )}
          {i.event_date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {new Date(i.event_date).toLocaleDateString("sv-SE")}
            </div>
          )}
          {i.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {i.location}
            </div>
          )}
          {i.guests != null && (
            <div className="flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
              {i.guests} gäster
            </div>
          )}
        </div>

        {i.message && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
              Meddelande
            </div>
            <p className="text-sm whitespace-pre-wrap rounded-lg bg-secondary/40 p-3">
              {i.message}
            </p>
          </div>
        )}

        {i.products.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Produkter
            </div>
            <div className="flex flex-wrap gap-2">
              {i.products.map((p, k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs"
                >
                  {p.name}
                  {p.quantity ? ` × ${p.quantity}` : ""}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-border">
          <span className="text-xs font-semibold text-muted-foreground mr-2">
            Status:
          </span>
          {Object.keys(STATUS_LABELS).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                i.status === s
                  ? STATUS_STYLES[s]
                  : "bg-secondary hover:bg-secondary/70"
              }`}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
